import React from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Chip,
  Button,
  alpha,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Search,
  Close,
  FilterAlt,
  Restaurant,
  LocalFlorist,
  LocalFireDepartment,
} from "@mui/icons-material";
import { LUXURY } from "data/guestLuxuryTheme";

const MobileFilterDrawer = ({
  luxury = false,
  open,
  onClose,
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  categories,
  dietaryFilter,
  onDietaryFilterChange,
  onClearFilters,
}) => {
  const theme = useTheme();

  const dietaryConfig = [
    {
      key: "vegetarian",
      color: "#047857",
      label: "Vegetarian",
      icon: <LocalFlorist sx={{ fontSize: 16 }} />,
    },
    {
      key: "vegan",
      color: "#0891b2",
      label: "Vegan",
      icon: <LocalFlorist sx={{ fontSize: 16 }} />,
    },
    {
      key: "spicy",
      color: "#d97706",
      label: "Spicy",
      icon: <LocalFireDepartment sx={{ fontSize: 16 }} />,
    },
  ];

  const hasActiveFilters =
    searchQuery || dietaryFilter.length > 0 || selectedCategory !== "all";

  const handleToggleDietary = (filter) => {
    if (dietaryFilter.includes(filter)) {
      onDietaryFilterChange(dietaryFilter.filter((f) => f !== filter));
    } else {
      onDietaryFilterChange([...dietaryFilter, filter]);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          borderTopLeftRadius: luxury ? "28px" : "24px",
          borderTopRightRadius: luxury ? "28px" : "24px",
          maxHeight: "85vh",
          bgcolor: luxury ? LUXURY.page : "#ffffff",
        },
      }}
    >
      <Box sx={{ p: 2.5, pb: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: luxury
                  ? LUXURY.barGradient
                  : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterAlt sx={{ color: "white", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, fontSize: "1rem", color: luxury ? LUXURY.ink : "inherit" }}
              >
                Filters & Search
              </Typography>
              {hasActiveFilters && (
                <Typography
                  variant="caption"
                  color={luxury ? LUXURY.orangeDark : "primary"}
                  sx={{ fontSize: "0.7rem", fontWeight: 800 }}
                >
                  {dietaryFilter.length + (selectedCategory !== "all" ? 1 : 0)}{" "}
                  active
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="medium">
            <Close />
          </IconButton>
        </Box>

        {/* Search Section */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "#374151",
              fontSize: "0.8rem",
              mb: 1.25,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Search sx={{ fontSize: 16, color: luxury ? LUXURY.forest : theme.palette.primary.main }} />
            Search Menu
          </Typography>
          <TextField
            fullWidth
            placeholder="Search dishes..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "#9ca3af", fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => onSearchChange("")}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: luxury ? "22px" : "14px",
                fontSize: "0.9rem",
                bgcolor: luxury ? LUXURY.card : "#f9fafb",
                height: 48,
                "& fieldset": {
                  borderColor: luxury ? LUXURY.cardBorder : alpha(theme.palette.divider, 0.5),
                },
                "&:hover fieldset": {
                  borderColor: luxury ? LUXURY.forest : theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: luxury ? LUXURY.forest : theme.palette.primary.main,
                  borderWidth: 2,
                },
              },
            }}
          />
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {/* Categories Section */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "#374151",
              fontSize: "0.8rem",
              mb: 1.25,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Restaurant
              sx={{ fontSize: 16, color: luxury ? LUXURY.forest : theme.palette.primary.main }}
            />
            Categories
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Chip
              label="All Items"
              onClick={() => onCategoryChange("all")}
              sx={{
                height: 40,
                fontSize: "0.85rem",
                fontWeight: 600,
                borderRadius: luxury ? "999px" : "12px",
                px: 2,
                bgcolor:
                  selectedCategory === "all"
                    ? luxury ? LUXURY.forest : theme.palette.primary.main
                    : luxury ? LUXURY.card : "#ffffff",
                color: selectedCategory === "all"
                  ? luxury ? LUXURY.cream : "#ffffff"
                  : luxury ? LUXURY.muted : "#6b7567",
                border: `1px solid ${selectedCategory === "all"
                  ? luxury ? LUXURY.forest : theme.palette.primary.main
                  : luxury ? LUXURY.cardBorder : alpha(theme.palette.divider, 0.3)}`,
                boxShadow: "none",
                "&:hover": {
                  bgcolor:
                    selectedCategory === "all"
                      ? luxury ? LUXURY.forestDeep : theme.palette.primary.dark
                      : luxury ? "#F3EDE3" : alpha(theme.palette.primary.main, 0.08),
                  borderColor: luxury ? LUXURY.forest : theme.palette.primary.main,
                },
                transition: "all 0.2s ease-out",
                cursor: "pointer",
              }}
            />
            {categories.map((category) => {
              const count = (category.MenuItems || []).filter(
                (item) => item.isActive,
              ).length;
              return (
                <Chip
                  key={category.id}
                  label={`${category.name} (${count})`}
                  onClick={() => onCategoryChange(category.id)}
                  sx={{
                    height: 40,
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    borderRadius: luxury ? "999px" : "12px",
                    px: 2,
                    bgcolor:
                      selectedCategory === category.id
                        ? luxury ? LUXURY.forest : theme.palette.primary.main
                        : luxury ? LUXURY.card : "#ffffff",
                    color:
                      selectedCategory === category.id
                        ? luxury ? LUXURY.cream : "#ffffff"
                        : luxury ? LUXURY.muted : "#6b7567",
                    border: `1px solid ${selectedCategory === category.id
                      ? luxury ? LUXURY.forest : theme.palette.primary.main
                      : luxury ? LUXURY.cardBorder : alpha(theme.palette.divider, 0.3)}`,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor:
                        selectedCategory === category.id
                          ? luxury ? LUXURY.forestDeep : theme.palette.primary.dark
                          : luxury ? "#F3EDE3" : alpha(theme.palette.primary.main, 0.08),
                      borderColor: luxury ? LUXURY.forest : theme.palette.primary.main,
                    },
                    transition: "all 0.2s ease-out",
                    cursor: "pointer",
                  }}
                />
              );
            })}
          </Box>
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {/* Dietary Filters Section */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "#374151",
              fontSize: "0.8rem",
              mb: 1.25,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <LocalFlorist
              sx={{ fontSize: 16, color: luxury ? LUXURY.forest : theme.palette.primary.main }}
            />
            Dietary Preferences
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {dietaryConfig.map((diet) => {
              const isSelected = dietaryFilter.includes(diet.key);
              return (
                <Chip
                  key={diet.key}
                  icon={isSelected ? diet.icon : undefined}
                  label={diet.label}
                  onClick={() => handleToggleDietary(diet.key)}
                  sx={{
                    height: 40,
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    borderRadius: luxury ? "999px" : "12px",
                    px: 2,
                    bgcolor: isSelected ? diet.color : "#ffffff",
                    color: isSelected ? "#ffffff" : diet.color,
                    border: `2px solid ${isSelected ? diet.color : alpha(diet.color, 0.3)}`,
                    boxShadow: isSelected
                      ? `0 4px 12px ${alpha(diet.color, 0.25)}`
                      : "none",
                    "& .MuiChip-icon": {
                      color: "#ffffff",
                    },
                    "&:hover": {
                      bgcolor: isSelected
                        ? diet.color
                        : alpha(diet.color, 0.08),
                      borderColor: diet.color,
                    },
                    transition: "all 0.2s ease-out",
                    cursor: "pointer",
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          {hasActiveFilters && (
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                onClearFilters();
                onClose();
              }}
              sx={{
                py: 1.25,
                borderRadius: "12px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.9rem",
                borderWidth: 2,
                "&:hover": {
                  borderWidth: 2,
                },
              }}
            >
              Clear All
            </Button>
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={onClose}
            sx={{
              py: 1.25,
                borderRadius: luxury ? "999px" : "12px",
                fontWeight: 800,
                textTransform: "none",
                fontSize: "0.9rem",
                background: luxury ? LUXURY.ctaGradient : undefined,
                boxShadow: luxury
                  ? LUXURY.ctaShadow
                  : `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                "&:hover": {
                  background: luxury ? LUXURY.ctaGradientHover : undefined,
                  boxShadow: luxury
                    ? LUXURY.ctaShadowHover
                    : `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
            }}
          >
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default MobileFilterDrawer;
